import React, { useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import events_service from "../../services/events_service";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);


const EventDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [infoList, setInfoList] = React.useState<any>({});
  const [closedList, setClosedList] = React.useState<any>({});
  const [eventName, setEventName] = React.useState<{
    name_en: string;
    name_am: string;
  }>({
    name_en: "",
    name_am: "",
  });
  const [hasSeatmap, setHasSeatmap] = React.useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const getEventReport = () => {
    events_service
      .getEventReport({
        id: id,
      })
      .then((res) => {
        const infoListArr = res?.data?.status?.infolist || {};
        setInfoList(infoListArr);
        const closedData = res?.data?.status?.closedlist || {};
        setClosedList(closedData);
        const hasSeatmap =
          res?.data?.status?.hasseatmap == "true" ? true : false;
        setHasSeatmap(hasSeatmap);
        setEventName({
          name_en: res?.data?.status?.eventName_en || "",
          name_am: res?.data?.status?.eventName_am || "",
        });
      })
      .catch((error) => {
        console.error("Error fetching event report:", error);
        setInfoList({});
        setClosedList({});
      });
  };
  useEffect(() => {
    getEventReport();
  }, [id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const decodeUnicode = (str: string) => {
    if (!str) return str;
    try {
      return str.replace(/u([0-9a-fA-F]{4})/g, (match, code) => {
        return String.fromCharCode(parseInt(code, 16));
      });
    } catch (error) {
      return str;
    }
  };

  const closedTickets = Object.keys(closedList ?? {}).map((dateKey) => {
    const ticketsArray = closedList[dateKey];

    // Calculate total price and total count based on event type
    let total = 0;
    let totalCount = 0;

    const mappedOrders = ticketsArray.map((ticket: any) => {
      const quantity = ticket.quantity || 1;
      const price = Number(ticket.price) || 0;

      // For events with seatmap, show row and seat
      if (hasSeatmap && ticket.row && ticket.seat) {
        total += price;
        totalCount += 1;
        return {
          seat: `${t("Row")} ${ticket.row} ${t("Seat")} ${ticket.seat}`,
          price: price,
          quantity: 1,
        };
      }
      // For events without seatmap, show quantity
      else {
        total += price * quantity;
        totalCount += quantity;
        return {
          seat: `${t("Quantity")} ${quantity}`,
          price: price,
          quantity: quantity,
        };
      }
    });

    return {
      dateKey: dateKey,
      when: formatDate(dateKey),
      order: mappedOrders,
      total: total,
      totalCount: totalCount,
      comment: ticketsArray[0]?.comment || "",
    };
  }).sort((a, b) => {
    // Sort by date from latest to oldest
    const dateA = new Date(a.dateKey).getTime();
    const dateB = new Date(b.dateKey).getTime();
    return dateB - dateA;
  });

  const handleRemoveClosedOrder = (commentDate: string) => {
    events_service
      .removeClosedOrders({
        event_id: id,
        commenttdate: commentDate,
      })
      .then(() => {
        MySwal.fire({
          icon: "success",
          title: t("Closed seat successfully opened"),
          showConfirmButton: false,
          timer: 1500,
          customClass: {
            popup: "custom-swal-popup",
            container: "custom-swal-container",
          },
        });
        getEventReport();
      })
      .catch(() => {
        MySwal.fire({
          icon: "error",
          title: t("Failed to open closed seat"),
          showConfirmButton: false,
          timer: 1500,
          customClass: {
            popup: "custom-swal-popup",
            container: "custom-swal-container",
          },
        });
      });
  };
  const totals = Object.keys(infoList ?? {}).reduce(
    (acc, key) => {
      const item = infoList[key];
      acc.Ordered += item.Ordered || 0;
      acc.Ordered_price += item.Ordered_price || 0;
      acc.Open += item.Open || 0;
      acc.Open_price += item.Open_price || 0;
      acc.Closed += item.Closed || 0;
      acc.Closed_price += item.Closed_price || 0;
      acc.Invitation += item.Invitation || 0;
      acc.Invitation_price += item.Invitation_price || 0;
      acc.Count += item.Count || 0;
      acc.TotalPrice += item.TotalPrice || 0;
      return acc;
    },
    {
      Ordered: 0,
      Ordered_price: 0,
      Open: 0,
      Open_price: 0,
      Closed: 0,
      Closed_price: 0,
      Invitation: 0,
      Invitation_price: 0,
      Count: 0,
      TotalPrice: 0,
    }
  );
  return (
    <Box sx={{ width: "100%", paddingBottom: "3rem" }}>
      <Box sx={{
        backgroundColor: "common.white",
        borderRadius: 1,
        padding: "1rem",
        boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.1)",
      }}>
        <Card sx={{
          padding: 2,
          marginBottom: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }} elevation={0}>
          <Typography variant="h5" sx={{ color: "black" }}>
            {i18n.language === "en" ? eventName?.name_en : eventName?.name_am}
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#4D67EBE6",
              color: "common.white",
              "&:hover": {
                backgroundColor: "#4D67EB",
              },
            }}
            size="medium"
            onClick={() => navigate("/events")}
          >
            {t("Back to events list")}
          </Button>
        </Card>
        <TableContainer component={Paper} sx={{ border: "1px solid #ddd" }}>
          <Table sx={{
            borderCollapse: "collapse",
            "& td, & th": {
              border: "1px solid #ddd",
              padding: 1.5,
              textAlign: "center",
            },
          }}>
            <TableHead>
              <TableRow>
                <TableCell rowSpan={2}>{t("Price")}</TableCell>
                <TableCell colSpan={2}>{t("Ordered")}</TableCell>
                <TableCell colSpan={2}>{t("Free Tickets")}</TableCell>
                <TableCell colSpan={2}>{t("Discontinued")}</TableCell>
                <TableCell colSpan={2}>{t("Invitations")}</TableCell>
                <TableCell colSpan={2}>{t("All")}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{t("Count")}</TableCell>
                <TableCell>{t("Sum")}</TableCell>
                <TableCell>{t("Count")}</TableCell>
                <TableCell>{t("Sum")}</TableCell>
                <TableCell>{t("Count")}</TableCell>
                <TableCell>{t("Sum")}</TableCell>
                <TableCell>{t("Count")}</TableCell>
                <TableCell>{t("Sum")}</TableCell>
                <TableCell>{t("Count")}</TableCell>
                <TableCell>{t("Sum")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.keys(infoList ?? {}).map((priceKey) => {
                const info = infoList[priceKey];
                return (
                  <TableRow key={priceKey}>
                    <TableCell>{priceKey}</TableCell>
                    <TableCell>{info.Ordered}</TableCell>
                    <TableCell>{info.Ordered_price}</TableCell>
                    <TableCell>{info.Open}</TableCell>
                    <TableCell>{info.Open_price}</TableCell>
                    <TableCell>{info.Closed}</TableCell>
                    <TableCell>{info.Closed_price}</TableCell>
                    <TableCell>{info.Invitation}</TableCell>
                    <TableCell>{info.Invitation_price}</TableCell>
                    <TableCell>{info.Count}</TableCell>
                    <TableCell>{info.TotalPrice}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow>
                <TableCell>{t("Total")}</TableCell>
                <TableCell>{totals.Ordered}</TableCell>
                <TableCell>{totals.Ordered_price}</TableCell>
                <TableCell>{totals.Open}</TableCell>
                <TableCell>{totals.Open_price}</TableCell>
                <TableCell>{totals.Closed}</TableCell>
                <TableCell>{totals.Closed_price}</TableCell>
                <TableCell>{totals.Invitation}</TableCell>
                <TableCell>{totals.Invitation_price}</TableCell>
                <TableCell>{totals.Count}</TableCell>
                <TableCell>{totals.TotalPrice}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="h6" sx={{
          marginTop: 2,
          marginBottom: 2,
          fontWeight: "bold",
        }}>
          {t("Closed Seats List")}
        </Typography>
        <TableContainer component={Paper} sx={{ border: "1px solid #ddd" }}>
          <Table sx={{
            borderCollapse: "collapse",
            "& td, & th": {
              border: "1px solid #ddd",
              padding: 1.5,
              textAlign: "center",
            },
          }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("When")}</TableCell>
                <TableCell>{t("Order")}</TableCell>
                <TableCell>{t("Comment")}</TableCell>
                <TableCell>{t("Action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {closedTickets.map((ticket, index) => {
                return (
                  <TableRow key={index}>
                    <TableCell>{ticket.when}</TableCell>
                    <TableCell>
                      <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        paddingLeft: 1,
                      }}>
                        {ticket.order.map((item: any, idx: number) => (
                          <div key={idx}>
                            {item.seat} - ֏{item.price}
                            {!hasSeatmap && item.quantity > 1 && ` (Total: ֏${item.price * item.quantity})`}
                          </div>
                        ))}
                        <Typography>
                          <strong>{t("Total")}</strong> {ticket.totalCount}{" "}
                          {hasSeatmap ? t("Seats") : t("Tickets")}, ֏{ticket.total}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{decodeUnicode(ticket.comment)}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: "error.main",
                          color: "common.white",
                          "&:hover": {
                            backgroundColor: "error.dark",
                          },
                        }}
                        size="small"
                        onClick={() => handleRemoveClosedOrder(ticket.dateKey)}
                      >
                        {t("Remove Order")}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default EventDetailsPage;
